import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Award, DollarSign, Target, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API_URL } from '../../lib/api-client';

interface PerformanceData {
  period: string;
  totalLeadsAssigned: number;
  totalLeadsConverted: number;
  conversionRate: number;
  totalCommissions: number;
  teamPerformance: {
    userId: string;
    name: string;
    leadsAssigned: number;
    leadsConverted: number;
    commissions: number;
    conversionRate: number;
  }[];
  monthlyTrend: {
    month: string;
    leadsAssigned: number;
    leadsConverted: number;
    commissions: number;
  }[];
}

export default function PerformanceDashboard() {
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('this_month');

  useEffect(() => {
    fetchPerformanceData();
  }, [selectedPeriod]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/marketing-manager/performance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: selectedPeriod }
      });
      setPerformanceData(data.performance);
    } catch (error: any) {
      console.error('Error fetching performance data:', error);
      toast.error('Failed to fetch performance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-8">
          <div className="text-center text-gray-500">Loading performance data...</div>
        </CardContent>
      </Card>
    );
  }

  if (!performanceData) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-8">
          <div className="text-center text-gray-500">No performance data available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Select Period</h3>
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Leads Assigned
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{performanceData.totalLeadsAssigned}</div>
              <Target className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Leads Converted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{performanceData.totalLeadsConverted}</div>
              <Award className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Conversion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{performanceData.conversionRate.toFixed(1)}%</div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Total Commissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">₹{performanceData.totalCommissions.toFixed(2)}</div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Performance Leaderboard */}
      <Card className="shadow-sm">
        <CardHeader className="bg-white dark:bg-gray-800 border-b">
          <CardTitle>Team Performance Leaderboard</CardTitle>
          <CardDescription>
            Individual performance metrics for your team
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {performanceData.teamPerformance.length > 0 ? (
            <div className="space-y-4">
              {performanceData.teamPerformance.map((member, index) => (
                <div key={member.userId} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white font-bold">
                    #{index + 1}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{member.name}</h3>
                    <div className="grid grid-cols-4 gap-4 mt-2">
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Assigned</p>
                        <p className="font-medium">{member.leadsAssigned}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Converted</p>
                        <p className="font-medium text-green-600">{member.leadsConverted}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Rate</p>
                        <p className="font-medium">{member.conversionRate.toFixed(1)}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Commissions</p>
                        <p className="font-medium text-green-600">₹{member.commissions.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  {index === 0 && (
                    <Award className="h-8 w-8 text-yellow-500" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No team performance data available for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Trend */}
      {performanceData.monthlyTrend && performanceData.monthlyTrend.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="bg-white dark:bg-gray-800 border-b">
            <CardTitle>Monthly Trend</CardTitle>
            <CardDescription>
              Performance over time
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {performanceData.monthlyTrend.map((month) => (
                <div key={month.month} className="flex items-center justify-between p-4 border rounded-lg bg-white dark:bg-gray-800 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <span className="font-medium">{month.month}</span>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-right">
                      <p className="text-gray-600 dark:text-gray-400">Assigned</p>
                      <p className="font-bold">{month.leadsAssigned}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-600 dark:text-gray-400">Converted</p>
                      <p className="font-bold text-green-600">{month.leadsConverted}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-600 dark:text-gray-400">Earnings</p>
                      <p className="font-bold text-green-600">₹{month.commissions.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}